import { useCallback } from "react";

import { useSelector } from "react-redux";

import {
    StyledLeadsBaseContentContainer,
    StyledLeadsTabContent,
    StyledLeadsTableWrapper,
} from "app/pages/sales-intelligence/common-components/layout";
import { SearchResultsTableBoundary } from "app/pages/sales-intelligence/common-components/search-results-table";
import type { TabContentComponentProps } from "app/pages/sales-intelligence/common-components/tabbed-page-content";
import type { PageTabBase } from "app/pages/sales-intelligence/common-components/tabbed-page-content/types";
import AppsLockedTableOverlay from "app/pages/sales-intelligence/modules/apps-search/components/feature-hook/AppsLockedTableOverlay";
import AppsExcelExportPanelContainer from "app/pages/sales-intelligence/modules/apps-search/containers/AppsExcelExportPanel/AppsExcelExportPanelContainer";
import AppsSelectionModalContainer from "app/pages/sales-intelligence/modules/apps-search/containers/AppsSelectionModal/AppsSelectionModalContainer";
import AppsTableActionsPanelContainer from "app/pages/sales-intelligence/modules/apps-search/containers/AppsTableActionsPanel/AppsTableActionsPanelContainer";
import { useAppsColumnSelectionPlugins } from "app/pages/sales-intelligence/modules/apps-search/hooks/useAppsColumnSelectionPlugins";
import { useAppsSearchResultsActionsState } from "app/pages/sales-intelligence/modules/apps-search/hooks/useAppsSearchResultsActionsState";
import { useClearAndCloseAppsRowsSelection } from "app/pages/sales-intelligence/modules/apps-search/hooks/useClearAndCloseAppsRowsSelection";
import { useAppsExcelExportMutation } from "app/pages/sales-intelligence/modules/apps-search/query/mutations/useAppsExcelExportMutation";
import { selectAppsSearchRequestDto } from "app/pages/sales-intelligence/modules/apps-search/store/selectors";
import { useIsLeadsPanelDataLoading } from "app/pages/sales-intelligence/modules/leads-search/hooks/useIsLeadsPanelDataLoading";
import { LeadsSearchFiltersWithAgent } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { useResetTerritory } from "app/pages/sales-intelligence/modules/teams/hooks/useResetTerritory";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";

import { TerritoryFiltersControlGate } from "../../components/TerritoryFiltersControlGate/TerritoryFiltersControlGate";
import { advancedSearchQueryKeyFactory } from "../../query/queryKeyFactory";
import type { AppsTabId } from "../../types/page-tabs";
import AppsSearchResultsTableContainer from "../SearchResultsTableContainer/AppsSearchResultsTableContainer";
import AppsTableSearchRowContainer from "../TableSearchRow/AppsTableSearchRowContainer";

type Props = TabContentComponentProps<PageTabBase<AppsTabId>>;

const AppsTabContentContainer = ({ shouldRenderDeferredContent = true }: Props) => {
    const isAppsLocked = !salesSettingsHelper.isConsolidatedAppsEnabled;
    const isLeadsPanelDataLoading = useIsLeadsPanelDataLoading();
    const resetTerritory = useResetTerritory();

    const dto = useSelector(selectAppsSearchRequestDto);

    useClearAndCloseAppsRowsSelection();

    const { areActionsDisabled, isTableLoading, numberOfResults } = useAppsSearchResultsActionsState(
        advancedSearchQueryKeyFactory.appsTableData(dto),
    );

    const { mutate, isPending: isExcelExportPending } = useAppsExcelExportMutation(
        dto,
        salesApiService.advancedSearchPage.apps.exportToExcel,
    );

    const { config: pluginConfig, pluginCleanup } = useAppsColumnSelectionPlugins();

    const renderSelectionModal = useCallback(
        (modalProps: { isOpen: boolean; closeModal: () => void }) => (
            <AppsSelectionModalContainer
                {...modalProps}
                pluginConfig={pluginConfig}
                onClose={pluginCleanup}
            />
        ),
        [pluginConfig, pluginCleanup],
    );

    return (
        <StyledLeadsTabContent data-automation="tab-content">
            <StyledLeadsBaseContentContainer>
                <LeadsSearchFiltersWithAgent
                    isLoading={isLeadsPanelDataLoading || !shouldRenderDeferredContent}
                    belowSearchSlot={<TerritoryFiltersControlGate />}
                    onClearAllFilters={resetTerritory}
                />
            </StyledLeadsBaseContentContainer>

            <StyledLeadsTableWrapper>
                <AppsTableActionsPanelContainer
                    areTableActionsDisabled={areActionsDisabled || isAppsLocked}
                    renderExportPanel={(panelProps) => (
                        <AppsExcelExportPanelContainer
                            {...panelProps}
                            numberOfResults={numberOfResults}
                            isLoading={isTableLoading || isExcelExportPending}
                            onExport={mutate}
                        />
                    )}
                    renderSelectionModal={renderSelectionModal}
                />
                <AppsTableSearchRowContainer areTableActionsDisabled={isAppsLocked} />

                <AppsLockedTableOverlay isAppsLocked={isAppsLocked}>
                    <SearchResultsTableBoundary shouldSuspend={!shouldRenderDeferredContent}>
                        <AppsSearchResultsTableContainer searchRequestDto={dto} />
                    </SearchResultsTableBoundary>
                </AppsLockedTableOverlay>
            </StyledLeadsTableWrapper>
        </StyledLeadsTabContent>
    );
};

export default AppsTabContentContainer;
